import { TranslationKey } from './../../../types/translation/TranslationKey';
import { ProductMasterData } from '../../../types/product_master_data/productMasterData';
import { t, translate } from '../../../types/translation/Translator';
import { User } from '../../../types/user/user';
import { toMap } from '../../../util/map.util';
import { TableHeader } from '../../components/Table';
import {
  AssetTransactionColumn,
  AssetTransactionTableSettings,
} from '../../../types/asset_transaction/assetTransaction.table';
import {
  AssetTransaction,
  AssetTransactionParentType,
  AssetTransactionStatus,
  AssetTransactionType,
} from '../../../types/asset_transaction/assetTransaction';
import { Grid } from '@mui/material';
import React from 'react';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AssetTransactionStatusLabel from './AssetTransactionStatusLabel';
import StatusLabel from '../../../VentoryUI/components/common/StatusLabel';
import { Colors } from '../../../VentoryUI/util/color.util';
import { Company } from '../../../types/company';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import OutboxIcon from '@mui/icons-material/Outbox';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';
import { Icon } from '../../icons';
import { IconType } from '../../icons/Icon';
import { UnitOfMeasure } from '../../../types/uom';
import dayjs from 'dayjs';

export namespace AssetTransactionTable {
  export const iconForType = (item: AssetTransaction, ignoreParentType: boolean = false) => {
    const color = item.status !== AssetTransactionStatus.completed ? 'text-gray-500' : undefined;

    if (item.parentType === AssetTransactionParentType.ownership && !ignoreParentType) {
      if (item.status === AssetTransactionStatus.completed) {
        return <Icon type={IconType.check} stroke={'stroke-green-500'} size={'h-5 w-5'} />;
      }
      return <Icon type={IconType.searchRefraction} stroke={'stroke-gray-500'} size={'h-5 w-5'} />;
    }

    if (item.parentType === AssetTransactionParentType.reassign && !ignoreParentType) {
      return <TransferWithinAStationIcon className={`${color || 'text-blue-500'}`} />;
    }

    if (item.parentType === AssetTransactionParentType.returnAsset && !ignoreParentType) {
      return <OutboxIcon className={`${color || 'text-yellow-500'}`} />;
    }

    if (item.type === AssetTransactionType.inbound && item.location && !ignoreParentType) {
      return <MoveToInboxIcon className={`${color || 'text-green-500'}`} />;
    }

    switch (item.type) {
      case AssetTransactionType.inbound:
        return <ArrowDownwardIcon className={`${color || 'text-green-500'}`} />;
      case AssetTransactionType.outbound:
        return <ArrowUpwardIcon className={`${color || 'text-yellow-500'}`} />;
    }
  };

  export const header = (
    productMasterData: Map<string, ProductMasterData>,
    users: Map<string, User>,
    company: Company,
    settings: AssetTransactionTableSettings,
    locale: string,
  ) => {
    const wantedColumns = toMap(settings.columns, 'column');

    const columns: TableHeader<AssetTransaction>[] = [];

    if (company.partner) {
      columns.push({
        key: 'company',
        name: translate({ key: TranslationKey.company }),
        text: (item: AssetTransaction) => company.name,
      });
    }

    if (wantedColumns.has(AssetTransactionColumn.productName)) {
      columns.push({
        key: 'name',
        name: wantedColumns.get(AssetTransactionColumn.productName)?.label || '',
        text: (item: AssetTransaction) =>
          productMasterData.get(item.product.productMasterDataId)?.productName ||
          t(TranslationKey.unknownProductMasterData).singular.label,
        column: AssetTransactionColumn.productName,
      });
    }

    if (wantedColumns.has(AssetTransactionColumn.productNumber)) {
      columns.push({
        key: 'number',
        name: wantedColumns.get(AssetTransactionColumn.productNumber)?.label || '',
        text: (item: AssetTransaction) =>
          productMasterData.get(item.product.productMasterDataId)?.productNumber ||
          t(TranslationKey.unknownProductMasterData).singular.label,
        column: AssetTransactionColumn.productNumber,
      });
    }

    if (wantedColumns.has(AssetTransactionColumn.quantity)) {
      columns.push({
        key: 'quantity',
        name: wantedColumns.get(AssetTransactionColumn.quantity)?.label || '',
        text: (item: AssetTransaction) => (
          <Grid container columnSpacing={1} justifyContent={'flex-end'}>
            <Grid item marginY={'auto'} className='h-7'>
              <p
                title={item.product.quantity.toString()}
                className={`h-7 overflow-hidden text-ellipsis whitespace-nowrap py-1 text-sm`}
              >
                {UnitOfMeasure.toQuantityWithSuffix(
                  productMasterData.get(item.product.productMasterDataId)?.unitOfMeasure,
                  item.product.quantity.toString(),
                )}
              </p>
            </Grid>
            <Grid item marginY={'auto'}>
              {iconForType(item)}
            </Grid>
          </Grid>
        ),
        sortValue: (item: AssetTransaction) => item.product.quantity,
        column: AssetTransactionColumn.quantity,
      });
    }

    if (wantedColumns.has(AssetTransactionColumn.pendingQuantity)) {
      columns.push({
        key: 'pendingQuantity',
        name: wantedColumns.get(AssetTransactionColumn.pendingQuantity)?.label || '',
        text: (item: AssetTransaction) => (
          <Grid container columnSpacing={1} justifyContent={'flex-end'}>
            <Grid item marginY={'auto'} className='h-7'>
              <p
                title={item.product.quantity.toString()}
                className={`h-7 overflow-hidden text-ellipsis whitespace-nowrap py-1 text-sm`}
              >
                {UnitOfMeasure.toQuantityWithSuffix(
                  productMasterData.get(item.product.productMasterDataId)?.unitOfMeasure,
                  item.product.quantity.toString(),
                )}
              </p>
            </Grid>
            <Grid item marginY={'auto'}>
              {iconForType(item, true)}
            </Grid>
          </Grid>
        ),
        sortValue: (item: AssetTransaction) => item.product.quantity,
        column: AssetTransactionColumn.pendingQuantity,
      });
    }

    if (wantedColumns.has(AssetTransactionColumn.serial)) {
      columns.push({
        key: 'serial',
        name: wantedColumns.get(AssetTransactionColumn.serial)?.label || '',
        text: (item: AssetTransaction) => item.product.serial || '',
        column: AssetTransactionColumn.serial,
      });
    }

    if (wantedColumns.has(AssetTransactionColumn.status)) {
      columns.push({
        key: 'status',
        name: wantedColumns.get(AssetTransactionColumn.status)?.label || '',
        text: (item: AssetTransaction) => <AssetTransactionStatusLabel asset={item} />,
        column: AssetTransactionColumn.status,
        sortValue: (item: AssetTransaction) => item.status,
      });
    }

    if (wantedColumns.has(AssetTransactionColumn.sign)) {
      columns.push({
        key: 'sign',
        name: wantedColumns.get(AssetTransactionColumn.sign)?.label || '',
        text: (item: AssetTransaction) => {
          let label;
          switch (item.parentType) {
            case AssetTransactionParentType.returnAsset:
              label = t(TranslationKey.signReturn).singular.label;
              break;
            case AssetTransactionParentType.reassign:
              label =
                item.type === AssetTransactionType.outbound
                  ? t(TranslationKey.signReturn).singular.label
                  : t(TranslationKey.signAssign).singular.label;
              break;
            case AssetTransactionParentType.ownership:
              label = t(TranslationKey.assetTransactionSignVerifyLabel).singular.label;
              break;
            default:
              label = t(TranslationKey.signAssign).singular.label;
          }
          return (
            <StatusLabel
              text={label}
              color={{
                backgroundColor: Colors.getBackgroundColor(company, 'secondary'),
                color: Colors.getTextColor(company, 'secondary'),
              }}
            />
          );
        },
        column: AssetTransactionColumn.sign,
      });
    }

    if (wantedColumns.has(AssetTransactionColumn.assignedTo)) {
      columns.push({
        key: 'assignedTo',
        name: wantedColumns.get(AssetTransactionColumn.assignedTo)?.label || '',
        text: (item: AssetTransaction) => users.get(item.location?.entityId || '')?.email || '',
        column: AssetTransactionColumn.assignedTo,
      });
    }

    if (wantedColumns.has(AssetTransactionColumn.signedAt)) {
      columns.push({
        key: 'signedAt',
        name: wantedColumns.get(AssetTransactionColumn.signedAt)?.label || '',
        text: (item: AssetTransaction) => {
          if (!item.processedAt) return '';
          return `${new Date(item.processedAt).toLocaleDateString(locale)}`;
        },
        sortValue: (item: AssetTransaction) => (item.processedAt ? dayjs(item.processedAt) : null),
        column: AssetTransactionColumn.signedAt,
      });
    }

    if (wantedColumns.has(AssetTransactionColumn.createdAt)) {
      columns.push({
        key: 'createdAt',
        name: wantedColumns.get(AssetTransactionColumn.createdAt)?.label || '',
        text: (item: AssetTransaction) => {
          return `${new Date(item.createdAt).toLocaleDateString(locale)}`;
        },
        sortValue: (item: AssetTransaction) => dayjs(item.createdAt),
        column: AssetTransactionColumn.createdAt,
      });
    }

    return columns.sort(
      (a: TableHeader<AssetTransaction>, b: TableHeader<AssetTransaction>) =>
        (wantedColumns.get(a.column || '')?.index || -1) - (wantedColumns.get(b.column || '')?.index || -1),
    );
  };
}
